// Delad render-helper för /pris/<slug>-sidor (priskluster, Iter 4).
// Tar en PriceGuide och returnerar färdig route-config (head + component).
// Mönster: createFileRoute("/pris/<slug>")(priceGuideRoute(getPriceGuide("<slug>")))

import { Link } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { PRICE_DATA_BASIS } from "@/content/pricing-facts";
import { authorPath } from "@/content/editorial";
import { Button } from "@/components/site/Button";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { AnswerFaq } from "@/components/site/aeo/AnswerFaq";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { article, breadcrumbs, qaPage, speakable, aeoGeo } from "@/lib/seo/schema";
import { toFaqJsonLd } from "@/lib/aeo/answers";
import { lastmodFor } from "@/lib/seo/sitemap";
import { formatReviewed } from "@/lib/seo/reviewed";
import type { PriceGuide } from "@/lib/seo/priceGuides";

const PUBLISHED = "2026-05-28";

export function priceGuideRoute(g: PriceGuide) {
  const path = `/pris/${g.slug}`;
  const absUrl = `https://fenny.se${path}`;
  const reviewed = lastmodFor(path) ?? PUBLISHED;

  return {
    head: () => ({
      meta: [
        { title: g.metaTitle },
        { name: "description", content: g.metaDescription },
        { property: "og:title", content: g.metaTitle },
        { property: "og:description", content: g.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: absUrl },
      ],
      links: [{ rel: "canonical", href: absUrl }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path, name: g.metaTitle, description: g.metaDescription, dateModified: reviewed })) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            qaPage({
              question: g.question,
              answer: g.shortAnswer,
              path,
              authorName: "Fenny",
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            article({
              headline: g.title,
              description: g.metaDescription,
              path,
              datePublished: PUBLISHED,
              dateModified: reviewed,
              authorName: "Fenny",
              section: "Pris",
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(toFaqJsonLd(g.faqIds)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs([
              { name: "Hem", path: "/" },
              { name: "Priser", path: "/pris" },
              { name: g.breadcrumbLabel, path },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(speakable(["[data-quick-answer]", "h1"])),
        },
      ],
    }),
    component: () => <PriceGuidePage g={g} path={path} reviewed={reviewed} />,
  };
}

function PriceGuidePage({
  g,
  path,
  reviewed,
}: {
  g: PriceGuide;
  path: string;
  reviewed: string;
}) {
  return (
    <Section className="pt-32 pb-20">
      <div className="container-wide max-w-4xl">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
          {g.eyebrow}
        </p>
        <h1 className="mt-3 text-balance text-4xl font-semibold tracking-tight md:text-5xl">
          {g.title}
        </h1>

        <p className="mt-4 text-sm text-muted-foreground">
          Senast granskad {formatReviewed(reviewed)} av{" "}
          <Link to={authorPath("mattias")} className="underline underline-offset-2">
            {PRICE_DATA_BASIS.reviewer}
          </Link>
          , ansvarig utgivare. {PRICE_DATA_BASIS.sourceSentence}
        </p>

        <QuickAnswer text={g.shortAnswer} tone="mint" />

        <div className="mt-8 space-y-4 text-lg leading-relaxed text-muted-foreground">
          {g.intro.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {/* Prisspann-tabell */}
        <div className="mt-12 overflow-hidden rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Nivå</th>
                <th className="px-4 py-3 text-left font-medium">Prisspann</th>
                <th className="px-4 py-3 text-left font-medium">Vad ingår</th>
              </tr>
            </thead>
            <tbody>
              {g.tiers.map((t) => (
                <tr key={t.label} className="border-t border-white/10">
                  <td className="px-4 py-3 font-medium text-foreground">{t.label}</td>
                  <td className="px-4 py-3 font-medium text-mint">{t.range}</td>
                  <td className="px-4 py-3 text-muted-foreground">{t.scope}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Spann är generella branschspann ur Fenny offertdata och inte bindande
          priser. Exakt pris beror på ert specifika upplägg.
        </p>

        {/* Drivare + besparingar */}
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <h2 className="text-lg font-semibold text-foreground">Vad styr priset?</h2>
            <ul className="mt-4 space-y-3">
              {g.drivers.map((item) => (
                <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-mint/20 bg-mint/[0.04] p-6">
            <h2 className="text-lg font-semibold text-foreground">
              Få mer film för budgeten
            </h2>
            <ul className="mt-4 space-y-3">
              {g.savings.map((item) => (
                <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-mint" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-wrap gap-3">
          <Button to="/kontakt" variant="mint" arrow>
            Boka strategimöte
          </Button>
          <Button to="/priser" variant="outline" arrow>
            Räkna pris
          </Button>
          <Button to={g.relatedServicePath} variant="ghost" arrow>
            {g.relatedServiceLabel}
          </Button>
        </div>
      </div>

      {/* FAQ — samma svar som FAQPage JSON-LD */}
      <div className="container-wide max-w-4xl">
        <AnswerFaq
          answerIds={g.faqIds}
          eyebrow="Frågor & svar"
          title="Vanliga frågor om pris"
          sectionId="faq"
          className="px-0 pt-16"
        />
        <RelatedLinksRail sourcePath={path} title="Relaterat" />
      </div>
    </Section>
  );
}
